import Image from "next/image";
import React, { useState, useEffect } from "react";
import styles from "./SuccessStoryNext.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { MoveLeft, MoveRight } from "lucide-react";

interface SuccessStoryData {
  id: number;
  title: string;
  cards: Card[];
}

interface Card {
  id: number;
  title_description_image: {
    id: number;
    title_description: {
      id: number;
      title: string;
      description: string;
    };
    images: Image | null;
    mobiles_images: Image | null;
  };
  link_with_image: {
    id: number;
    link: {
      id: number;
      label: string;
      url: string;
    };
    image: Image;
    mobile_image?: Image | null;
  };
}

interface Image {
  alternativeText: string | null;
  url: string;
}

interface Props {
  data: SuccessStoryData;
}

const SuccessStoryNext: React.FC<Props> = ({ data }) => {
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    // Check if we're on the client side
    if (typeof window !== "undefined") {
      // Set initial state
      setIsMobile(window.innerWidth <= 768);

      // Add event listener for window resize
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      window.addEventListener("resize", handleResize);

      // Clean up event listener
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  const extractYouTubeID = (url: string): string | null => {
    const match = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    return match ? match[1] : null;
  };

  return (
    <section className={styles.nextContainer}>
      <div className={`${styles.topContainer} container`}>
        <h2 className={styles.title}>{data?.title}</h2>
        {data?.cards && data.cards.length > 1 && (
          <div className="d-none d-md-block">
            <div className="d-flex">
              <button
                className={`${styles.customArrow} ${styles.prevButton}`}
                aria-label="Previous Slide"
                style={{ marginRight: "10px" }}
              >
                <MoveLeft />
              </button>
              <button
                className={`${styles.customArrow} ${styles.nextButton}`}
                aria-label="Next Slide"
              >
                <MoveRight />
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="container">
        <Swiper
          spaceBetween={30}
          slidesPerView={1}
          navigation={{
            nextEl: `.${styles.nextButton}`,
            prevEl: `.${styles.prevButton}`,
          }}
          modules={[Navigation]}
          loop
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
        >
          {data?.cards?.map((card, index) => {
            const videoId = extractYouTubeID(card?.link_with_image?.link?.url);
            const isPlaying =
              playingVideoId === card?.id && activeSlide === index;

            // Generate YouTube thumbnail URL if we have a video ID
            let thumbnailUrl: string | null = null;
            if (videoId) {
              thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            }

            // Determine which image to use
            let imageUrl: string | null = null;
            let imageAlt: string | null = "Video thumbnail";

            if (isMobile && card?.link_with_image?.mobile_image?.url) {
              // Use mobile image if on mobile and mobile image exists
              imageUrl = card.link_with_image.mobile_image.url;
              imageAlt =
                card.link_with_image.mobile_image.alternativeText ||
                "Video thumbnail";
            } else if (card?.link_with_image?.image?.url) {
              // Use desktop image if it exists
              imageUrl = card.link_with_image.image.url;
              imageAlt =
                card.link_with_image.image.alternativeText || "Video thumbnail";
            } else if (thumbnailUrl) {
              // Use YouTube thumbnail as fallback
              imageUrl = thumbnailUrl;
            }

            return (
              <SwiperSlide key={card?.id}>
                <div className={styles.missionContent}>
                  <div className={styles.videoCard}>
                    {!isPlaying ? (
                      <div className={styles.cover}>
                        <div className={styles.overlay}></div>
                        {imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={imageAlt}
                            title={imageAlt}
                            fill
                            style={{
                              objectFit: "cover",
                            }}
                            className={styles.thumbnail}
                          />
                        ) : (
                          // If no image is available, show a placeholder
                          <div className={styles.noThumbnail}>
                            <span>Video</span>
                          </div>
                        )}
                        <button
                          aria-label="Play Button"
                          className={styles.playButton}
                          onClick={() => setPlayingVideoId(card?.id)}
                        >
                          ▶
                        </button>
                      </div>
                    ) : (
                      <div className={styles.videoBanner}>
                        <iframe
                          className={styles.youtubeVideo}
                          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&modestbranding=1&controls=1&rel=0&showinfo=0&iv_load_policy=3&fs=1&disablekb=1`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin"
                        ></iframe>
                      </div>
                    )}
                  </div>
                  <div className={styles.textContent}>
                    <h2>
                      {card?.title_description_image?.title_description?.title}
                    </h2>
                    <p>
                      {
                        card?.title_description_image?.title_description
                          ?.description
                      }
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {data?.cards && data.cards.length > 1 && (
        <div className="d-md-none d-block">
          <div className="d-flex align-items-center justify-content-center">
            <button
              className={`${styles.customArrow} ${styles.prevButton}`}
              aria-label="Previous Slide"
              style={{ marginRight: "10px" }}
            >
              <MoveLeft />
            </button>
            <button
              className={`${styles.customArrow} ${styles.nextButton}`}
              aria-label="Next Slide"
            >
              <MoveRight />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default SuccessStoryNext;
